import { useState, useEffect } from 'react';

/**
 * useResponsive Hook
 * 
 * Provides responsive breakpoint detection and utilities.
 * Automatically updates on window resize.
 * 
 * @returns {Object} Responsive state and utilities
 * 
 * @example
 * const { isMobile, isTablet, isDesktop, breakpoint } = useResponsive();
 * 
 * if (isMobile) {
 *   return <MobileView />;
 * }
 */
export const useResponsive = () => {
  const [breakpoint, setBreakpoint] = useState('desktop');
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    // Handler to update breakpoint based on window width
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      setWindowWidth(width);

      if (width < 768) {
        setBreakpoint('mobile');
      } else if (width < 1024) {
        setBreakpoint('tablet');
      } else if (width < 1440) {
        setBreakpoint('desktop');
      } else {
        setBreakpoint('wide');
      }
    };

    // Set initial breakpoint
    updateBreakpoint();

    // Add event listener
    window.addEventListener('resize', updateBreakpoint);

    // Cleanup
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  return {
    // Boolean flags for convenience
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop',
    isWide: breakpoint === 'wide',
    
    // Grouped flags
    isMobileOrTablet: breakpoint === 'mobile' || breakpoint === 'tablet',
    isDesktopOrWide: breakpoint === 'desktop' || breakpoint === 'wide',
    
    // Current breakpoint name
    breakpoint,
    
    // Raw window width
    windowWidth,
    
    // Utility functions
    isAtLeast: (bp) => {
      const order = { mobile: 0, tablet: 1, desktop: 2, wide: 3 };
      const currentOrder = order[breakpoint];
      const targetOrder = order[bp];
      return currentOrder >= targetOrder;
    },
    
    isAtMost: (bp) => {
      const order = { mobile: 0, tablet: 1, desktop: 2, wide: 3 };
      const currentOrder = order[breakpoint];
      const targetOrder = order[bp];
      return currentOrder <= targetOrder;
    },
  };
};

export default useResponsive;
